import axios, { AxiosInstance } from "axios";
import router from "@/router/router";
import { useAuthStore } from "../store/useAuthStore";

const CSRF_RETRY_FLAG = "__csrfRetried";

/**
 * Fetches the CSRF cookie from Sanctum.
 * - Cached for the lifetime of the session — only one real HTTP request is made.
 * - Concurrent callers share the same in-flight promise (no race conditions).
 * - Call resetCsrfCookie() to force a fresh fetch (e.g. after a 419 response).
 */
let csrfCookiePromise: Promise<void> | null = null;

// Prevents duplicate clearAuthSession() + router.push() calls when multiple
// in-flight requests all receive 401s simultaneously (e.g. during logout).
let isHandling401 = false;

export function resetCsrfCookie() {
    csrfCookiePromise = null;
}

export async function fetchCsrfCookie() {
    if (!csrfCookiePromise) {
        csrfCookiePromise = axios({
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            url: "/sanctum/csrf-cookie",
            baseURL: "/",
            withCredentials: true,
        })
            .then(() => undefined)
            .catch((err) => {
                // On failure, allow a retry next time
                csrfCookiePromise = null;
                return Promise.reject(err);
            });
    }

    await csrfCookiePromise;
}

/**
 * Shared factory — attaches CSRF and error interceptors to any axios instance.
 * Pass `withWorkspaceHeader: true` to also inject X-Workspace-Id (for /api/v1 calls).
 * Pass `withTourEvents: true` to fire GTM tour-published events on responses.
 */
function createInstance(
    baseURL: string,
    { withWorkspaceHeader = false, withTourEvents = false } = {},
): AxiosInstance {
    const authStore = useAuthStore();

    const instance = axios.create({
        baseURL,
        withCredentials: true,
        headers: {
            "X-Requested-With": "XMLHttpRequest",
            Accept: "application/json",
            "Content-Type": "application/json",
        },
    });

    // Ensures the CSRF cookie is set before every request (cached for the session).
    // Also injects X-Workspace-Id for multi-tab workspace isolation when required.
    instance.interceptors.request.use(async (config) => {
        await fetchCsrfCookie();
        if (withWorkspaceHeader) {
            const workspaceId = sessionStorage.getItem("tripcart_workspace_id");
            if (workspaceId) {
                config.headers["X-Workspace-Id"] = workspaceId;
            }
        }
        return config;
    });

    instance.interceptors.response.use(
        (response) => {
            if (withTourEvents && response.data?.is_first_tour_published === true) {
                const tripSlug = response.data.trip_slug;
                const tripName = response.data.trip_name;
                const seoTripPrefix = response.data.seo_trip_prefix || "tour";
                const agencyDomain = authStore.user?.agency_domain || "";
                const tourUrl = `${agencyDomain}/${seoTripPrefix}/${tripSlug}/`;
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: "tour_published",
                    event_id: `tour_${tripSlug}_${Date.now()}`,
                    tour_name: tripName,
                    tour_url: tourUrl,
                    user_id: `admin_${authStore.user?.id}`,
                    source: "admin",
                });
            }
            return response;
        },
        async (error) => {
            if (error.response?.status === 401) {
                if (!isHandling401) {
                    isHandling401 = true;
                    authStore.clearAuthSession();
                    router.push({ name: "login" }).finally(() => {
                        isHandling401 = false;
                    });
                }
                return Promise.reject(error);
            }
            else if (error.response?.status === 403) {
                router.push({ name: "forbidden" });
                return Promise.reject(error);
            }
            else if (error.response?.status === 419) {
                // CSRF token mismatch — refresh cookie and retry the request once.
                // If the retry itself gets a 419, give up and reject.
                if (!error.config[CSRF_RETRY_FLAG]) {
                    error.config[CSRF_RETRY_FLAG] = true;
                    resetCsrfCookie();
                    await fetchCsrfCookie();
                    return instance(error.config);
                }
                return Promise.reject(error);
            }
            else {
                return Promise.reject(error);
            }
        },
    );

    return instance;
}

/**
 * For /api/v1 endpoints — includes workspace header and tour GTM events.
 */
export default () => createInstance("/api/v1", { withWorkspaceHeader: true, withTourEvents: true });

/**
 * For root-level Sanctum routes (/login, /logout, etc.) — no workspace header or GTM events.
 */
export const WebApi = () => createInstance("/");
